import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { ImmunizationsFormField } from './formField'

/**
 * Immunizations subsection adapter
 * Handles vaccine/date pairs for various immunizations (13 fields)
 * This follows the MultiColumn pattern: vaccine + date pairs
 */
export class ImmunizationsAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hepatitis B vaccine/date pair
    const hepB = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B
    )?.at(0)

    const hepBDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE
    )?.at(0)

    // Meningococcal vaccine/date pair
    const meningococcal = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL
    )?.at(0)

    const meningococcalDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE
    )?.at(0)

    // COVID-19 vaccine/date pair
    const covid19 = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19
    )?.at(0)

    const covid19Date = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE
    )?.at(0)

    // DTaP vaccine/date pair
    const dtap = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP
    )?.at(0)

    const dtapDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE
    )?.at(0)

    // HPV vaccine/date pair
    const hpv = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV
    )?.at(0)

    const hpvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE
    )?.at(0)

    // RSV vaccine/date pair
    const rsv = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV
    )?.at(0)

    const rsvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE
    )?.at(0)

    // Notes field
    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES
    )?.at(0)

    return {
      [ImmunizationsFormField.IMMUNIZATIONS_HEP_B]: hepB,
      [ImmunizationsFormField.IMMUNIZATIONS_HEP_B_DATE]: hepBDate,
      [ImmunizationsFormField.IMMUNIZATIONS_MENINGOCOCCAL]: meningococcal,
      [ImmunizationsFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE]: meningococcalDate,
      [ImmunizationsFormField.IMMUNIZATIONS_COVID_19]: covid19,
      [ImmunizationsFormField.IMMUNIZATIONS_COVID_19_DATE]: covid19Date,
      [ImmunizationsFormField.IMMUNIZATIONS_DTAP]: dtap,
      [ImmunizationsFormField.IMMUNIZATIONS_DTAP_DATE]: dtapDate,
      [ImmunizationsFormField.IMMUNIZATIONS_HPV]: hpv,
      [ImmunizationsFormField.IMMUNIZATIONS_HPV_DATE]: hpvDate,
      [ImmunizationsFormField.IMMUNIZATIONS_RSV]: rsv,
      [ImmunizationsFormField.IMMUNIZATIONS_RSV_DATE]: rsvDate,
      [ImmunizationsFormField.IMMUNIZATIONS_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_HEP_B)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_MENINGOCOCCAL)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_COVID_19)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_DTAP)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_HPV)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_RSV)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_NOTES)
      }
    ]

    // Conditionally add date fields if they exist (they are conditionally rendered when vaccine === 'Yes')
    if (this.formDataFieldExists(ImmunizationsFormField.IMMUNIZATIONS_HEP_B_DATE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_HEP_B_DATE)
      })
    }

    if (this.formDataFieldExists(ImmunizationsFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)
      })
    }

    if (this.formDataFieldExists(ImmunizationsFormField.IMMUNIZATIONS_COVID_19_DATE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_COVID_19_DATE)
      })
    }

    if (this.formDataFieldExists(ImmunizationsFormField.IMMUNIZATIONS_DTAP_DATE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_DTAP_DATE)
      })
    }

    if (this.formDataFieldExists(ImmunizationsFormField.IMMUNIZATIONS_HPV_DATE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_HPV_DATE)
      })
    }

    if (this.formDataFieldExists(ImmunizationsFormField.IMMUNIZATIONS_RSV_DATE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE,
        value: this.getFormDataField(ImmunizationsFormField.IMMUNIZATIONS_RSV_DATE)
      })
    }

    return mutations
  }
}
